import { HttpErrorResponse } from '@angular/common/http';
import {
  computed,
  inject,
  Injectable,
  InjectionToken,
  linkedSignal,
  resource,
  signal,
  untracked,
} from '@angular/core';
import { clientLogger } from '@mb/common/client';

function toLoggedError(error: unknown): Error {
  if (error instanceof HttpErrorResponse) {
    return new Error(error.message);
  }
  if (error instanceof Error) {
    return error;
  }
  return new Error(String(error));
}

function rowsEqual<T>(a: readonly T[], b: readonly T[]): boolean {
  if (a === b) return true;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}

export type IdentifyBy<T> = (item: T) => string | number;
export const TABLE_IDENTIFY_BY = new InjectionToken<IdentifyBy<unknown>>(
  'TABLE_IDENTIFY_BY',
);

export type GetDetailsOptions = { signal?: AbortSignal };
export type GetDetails<T> = (
  id: string | number,
  options?: GetDetailsOptions,
) => Promise<T | null>;
export const TABLE_GET_DETAILS = new InjectionToken<GetDetails<unknown>>(
  'TABLE_GET_DETAILS',
);

export type TableSelectionMode = 'single' | 'multiple';
export type TableRowId = string | number;

@Injectable()
export class TableStateService<T> {
  readonly #identifyBy = inject(TABLE_IDENTIFY_BY) as IdentifyBy<T>;
  readonly #getDetails = inject(TABLE_GET_DETAILS, {
    optional: true,
  }) as GetDetails<T> | null;

  /** Rows loaded into the table (client-side or accumulated from infinite scroll). */
  readonly rows = signal<readonly T[]>([]);

  /** Bump to reset infinite scroll / reload the grid from the server. */
  readonly listRevision = signal(0);

  readonly #pendingListLoads = signal(0);
  readonly isListLoading = computed(() => this.#pendingListLoads() > 0);

  readonly listError = signal<Error | null>(null);

  readonly selectionMode = signal<TableSelectionMode>('single');
  readonly selectedIds = signal<ReadonlySet<TableRowId>>(new Set());
  readonly selectedCount = computed(() => this.selectedIds().size);
  readonly selectedId = computed(() => {
    const ids = this.selectedIds();
    if (ids.size === 0) {
      return null;
    }
    return ids.values().next().value ?? null;
  });

  readonly #detailsResource = resource<T | null, TableRowId | undefined>({
    params: () => {
      if (this.selectionMode() !== 'single') return undefined;
      if (!this.#getDetails) return undefined;
      return this.selectedId() ?? undefined;
    },
    loader: ({ params, abortSignal }) => {
      if (params === undefined || !this.#getDetails)
        return Promise.resolve(null);
      return this.#getDetails(params, { signal: abortSignal }).catch(
        (error) => {
          clientLogger.error('Failed to load details', {
            error: toLoggedError(error),
          });
          return null;
        },
      );
    },
  });

  readonly selectedDetails = computed<T | null>(
    () => this.#detailsResource.value() ?? null,
  );
  readonly isLoadingDetails = computed<boolean>(() =>
    this.#detailsResource.isLoading(),
  );

  readonly selectedRows = computed<readonly T[]>(() => {
    const ids = this.selectedIds();
    if (ids.size === 0) return [];
    const identifyBy = this.#identifyBy;
    return this.rows().filter((r) => ids.has(identifyBy(r)));
  });

  /**
   * Resolves the selected entity, holding the last resolved value while the same
   * id is selected. Without the hold, switching rows would briefly return null —
   * the details `resource()` resets to undefined on reload and the `rows()`
   * fallback is not reliably populated in infinite-scroll mode — which makes the
   * drawer content `@if` tear down and flicker. Keeping the previous value for the
   * same id keeps the content mounted; it only clears when the id actually changes.
   */
  readonly #resolvedSelection = linkedSignal<TableRowId | null, T | null>({
    source: () => this.selectedId(),
    computation: (id, previous) => {
      if (id == null || this.selectionMode() === 'multiple') {
        return null;
      }

      if (this.#getDetails && this.selectedDetails() !== null) {
        return this.selectedDetails();
      }

      const entity = this.rows().find(
        (r) => String(this.#identifyBy(r)) === String(id),
      );
      if (entity != null) {
        return entity;
      }

      // Same id still selected, just reloading: keep the last resolved value so
      // the drawer content does not blank out between A and B.
      if (previous && previous.source === id && previous.value != null) {
        return previous.value;
      }

      return null;
    },
  });

  readonly selected = computed<T | null>(() => this.#resolvedSelection());

  readonly isDrawerOpen = computed(
    () => this.selectionMode() === 'single' && this.selectedId() != null,
  );
  readonly drawerTitle = signal<string>('');

  refreshList(): void {
    this.listRevision.update((v) => v + 1);
  }

  beginListLoad(): void {
    this.#pendingListLoads.update((v) => v + 1);
  }

  endListLoad(error?: unknown): void {
    this.#pendingListLoads.update((v) => Math.max(0, v - 1));
    this.listError.set(error === undefined ? null : toLoggedError(error));
  }

  abortListLoad(): void {
    this.#pendingListLoads.update((v) => Math.max(0, v - 1));
  }

  clearListError(): void {
    this.listError.set(null);
  }

  /**
   * Replace the rows, if they actually changed.
   *
   * The comparison reads the current rows untracked on purpose. A caller
   * that feeds rows from an `effect` would otherwise take a dependency on
   * the very signal it is about to write: the write wakes the effect, the
   * effect builds fresh row objects which never compare equal, and the two
   * chase each other for ever. The application then never settles, and
   * everything waiting on it — the grid's own first render among them —
   * waits with it.
   */
  setRows(rows: readonly T[]): void {
    if (untracked(() => rowsEqual(this.rows(), rows))) {
      return;
    }
    this.rows.set(rows);
  }

  upsertRow(row: T): void {
    const id = this.#identifyBy(row);
    this.rows.update((list) => {
      const index = list.findIndex(
        (r) => String(this.#identifyBy(r)) === String(id),
      );
      if (index >= 0) {
        if (list[index] === row) {
          return list;
        }
        const next = [...list];
        next[index] = row;
        return next;
      }
      return [...list, row];
    });
  }

  upsertRows(rows: readonly T[]): void {
    for (const row of rows) {
      this.upsertRow(row);
    }
  }

  removeRow(id: TableRowId): void {
    this.rows.update((list) => {
      const next = list.filter(
        (r) => String(this.#identifyBy(r)) !== String(id),
      );
      return next.length === list.length ? list : next;
    });
  }

  /**
   * Patch the loaded detail entity locally (e.g. after a successful save).
   * Avoids a network reload when the mutation response is complete.
   */
  patchSelectedDetails(details: T): void {
    if (!this.#getDetails) {
      return;
    }
    this.#detailsResource.set(details);
  }

  reloadSelectedDetails(): void {
    if (
      this.selectionMode() === 'single' &&
      this.selectedId() &&
      this.#getDetails
    ) {
      this.#detailsResource.reload();
    }
  }

  setMode(mode: TableSelectionMode) {
    if (mode === this.selectionMode()) {
      return;
    }

    const currentIds = Array.from(this.selectedIds());
    this.selectionMode.set(mode);

    if (mode === 'single') {
      const firstId = currentIds[0] ?? null;
      this.selectedIds.set(firstId == null ? new Set() : new Set([firstId]));
    }
  }

  select(id: TableRowId) {
    if (this.selectionMode() === 'single') {
      this.selectedIds.set(new Set([id]));
    } else {
      const next = new Set(this.selectedIds());
      next.add(id);
      this.selectedIds.set(next);
    }
  }

  toggle(id: TableRowId) {
    if (this.selectionMode() === 'single') {
      this.select(id);
      return;
    }

    const next = new Set(this.selectedIds());
    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
    }
    this.selectedIds.set(next);
  }

  clear() {
    this.selectedIds.set(new Set());
  }

  setSelectedIds(ids: ReadonlySet<TableRowId>) {
    const next = new Set(ids);

    if (this.selectionMode() === 'single' && next.size > 1) {
      const first = next.values().next().value ?? null;
      this.selectedIds.set(first == null ? new Set() : new Set([first]));
      return;
    }

    this.selectedIds.set(next);
  }

  async selectById(id: TableRowId | null) {
    if (id == null) {
      this.clear();
      return;
    }

    if (this.selectionMode() === 'multiple') {
      this.toggle(id);
      return;
    }

    const row = this.rows().find(
      (r) => String(this.#identifyBy(r)) === String(id),
    );
    const canonicalId = row != null ? this.#identifyBy(row) : id;

    this.selectedIds.set(new Set([canonicalId]));
  }

  setDrawerTitle(title: string) {
    this.drawerTitle.set(title);
  }
}
